/* eslint-disable consistent-return */
import { cloneDeep, map } from 'lodash';
import { check } from 'meteor/check';

import { createAIAnnotation } from '../annotation-calculations';
import * as annotationOps from '../annotation-operations';
import Annotations from '../Annotations';

import checkAuthentication from '../../../modules/server/check-authentication';
import handleMethodException from '../../../modules/handle-method-exception';

const editAnnotationSchema = Annotations.schema.omit('owner', 'createdAt', 'updatedAt', 'deleted');
editAnnotationSchema.extend({ _id: String });

/**
 * Given a project, copies each of its videos and returns the
 * an array with the copied video ids.
 *
 * @param {string} videoId - The origin project id.
 * @returns {object[]} An array of objects with the original and new videos ids.
 */
export const annotationsFromVideo = async videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    const annoationsToCopy = Annotations.find({
      owner: this.userId,
      'geoJSON.properties.video': videoId
    }).fetch();
    return annoationsToCopy;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Inserts the annotation in the database.
 *
 * @param {object} annotation - The annotation object.
 * @param {string} userId - The user id that owns the annotation.
 * @returns {string} The id of the new annotation inserted in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = (annotation, userId) => {
  check(annotation, Object);
  check(userId, String);
  try {
    checkAuthentication();
    const clonedAnnotation = cloneDeep(annotation);
    clonedAnnotation.geoJSON.properties.owner = userId;
    Annotations.schema.validate(clonedAnnotation, {
      check,
      keys: ['geoJSON', 'origin']
    });
    const annotationId = Annotations.insert({
      owner: userId,
      ...clonedAnnotation
    });
    // This is used in order to have the id of the annotation in Postgres
    Annotations.update(annotationId, {
      $set: { 'geoJSON.properties._id': annotationId }
    });
    return annotationId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Inserts several annotations in the database.
 *
 * @param {object[]} annotations - The annotation objects.
 * @param {string} userId - The user id that owns the annotations.
 * @returns {string[]} The ids of the new annotations inserted in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insertMany = (annotations, userId) => {
  check(annotations, Array);
  check(userId, String);
  try {
    checkAuthentication();
    return map(annotations, annotation => insert(annotation, userId));
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the annotation in the database.
 *
 * @param {object} annotation - The new annotation object.
 * @param {string} userId - The user id that owns the annotation.
 * @returns {string} The id of the updated annotation in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = (annotation, userId) => {
  check(annotation, Object);
  try {
    checkAuthentication();
    const clonedAnnotation = cloneDeep(annotation);
    clonedAnnotation.geoJSON.properties.owner = userId;
    editAnnotationSchema.validate(clonedAnnotation);
    const annotationId = annotationOps.getId(annotation);
    Annotations.update(annotationId, {
      $set: { 'geoJSON.properties.template': annotation.geoJSON.properties.template }
    });
    return annotationId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Given an annotation and a video id, this method copies the annotation in the provided video.
 *
 * @param {object} annotation - The annotation object to copy.
 * @param {string} destVideoId - The video id.
 * @param {string} userId - The user id that owns the video.
 * @returns {string} Returns the id of the new annotation copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
const copyOne = (annotation, destVideoId, userId) => {
  try {
    checkAuthentication();
    const copyAnnotation = cloneDeep(annotation);
    delete copyAnnotation._id;
    delete copyAnnotation.owner;
    delete copyAnnotation.geoJSON.properties._id;
    delete copyAnnotation.geoJSON.properties.createdAt;
    delete copyAnnotation.geoJSON.properties.updatedAt;
    delete copyAnnotation.geoJSON.properties.video;
    copyAnnotation.geoJSON.properties.video = destVideoId;
    copyAnnotation.geoJSON.properties.owner = userId;
    const copyAnnotationId = Annotations.insert({
      owner: userId,
      ...copyAnnotation
    });
    // This is used in order to have the id of the annotation in Postgres
    Annotations.update(copyAnnotationId, {
      $set: { 'geoJSON.properties._id': copyAnnotationId }
    });
    return copyAnnotationId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Given a source video and a destination video, copies each annotation from the first one
 * to the second one and returns an array with the copied annotation ids.
 *
 * @async
 * @param {string} sourceVideoId - The source video id.
 * @param {string} destVideoId - The destination video id.
 * @param {string} userId - The user id that owns the destination video.
 * @returns {string[]} An array of strings with the new annotation ids.
 */
export const copy = async (sourceVideoId, destVideoId, userId) => {
  check(sourceVideoId, String);
  check(destVideoId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const annotations = Annotations.find({ 'geoJSON.properties.video': sourceVideoId }).fetch();
    const newAnnotationsPromises = map(annotations, annotation =>
      copyOne(annotation, destVideoId, userId)
    );
    const newAnnotations = await Promise.all(newAnnotationsPromises);
    return newAnnotations;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the annotation from the database.
 *
 * @param {string} annotationId - The annotation id.
 */
export const hardDelete = annotationId => {
  check(annotationId, String);
  try {
    checkAuthentication();
    Annotations.remove(annotationId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes many annotations from the database.
 *
 * @param {string} videoId - The annotation property for filter.
 */
export const hardManyDelete = videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    const annotationList = Annotations.find({ 'geoJSON.properties.video': videoId }).fetch();
    annotationList.map(item => Annotations.remove(item._id));
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the automatic annotations of a video from the database.
 *
 * @param {string} videoId - The video id.
 */
export const deleteAutomatic = videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    Annotations.remove({ origin: 'automatic', 'geoJSON.properties.video': videoId });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Creates annotations in the database for all the elements received from AI service.
 *
 * @param {string} videoId - The video id the annotations belong to.
 * @param {object[]} detectedEntities - An array with the elements received from AI service.
 * @param {object} template - The template for the annotations.
 * @param {object[]} featureArray - The coordinates of the video track.
 * @param {string} userId - The user id the annotations belong to.
 * @returns {string[]} An array with the ids of the new annotations inserted in the database.
 * @throws Error if something went wrong.
 */
export const saveVideoEntities = async (
  videoId,
  detectedEntities,
  template,
  featureArray,
  userId
) => {
  try {
    checkAuthentication();
    const newAnnotationsPromises = map(detectedEntities, entity => {
      const newAnnotation = createAIAnnotation(videoId, entity, template, featureArray);
      return insert(newAnnotation, userId);
    });
    const newAnnotations = await Promise.all(newAnnotationsPromises);
    return newAnnotations;
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Validate schema import file json y Maps, NotesComponent.
 *
 * @param {object} annotation - The annotation objects.
 * @param {string} userId - The user id that owns the annotations.
 */

export const checkFileJsonSchema = (annotation, userId) => {
  check(annotation, Object);
  check(userId, String);
  const checkFile = cloneDeep(annotation);
  try {
    checkAuthentication();
    checkFile.owner = userId;
    checkFile.geoJSON.properties.owner = userId;
    Annotations.schema.validate(checkFile, {
      check,
      keys: ['geoJSON', 'origin']
    });
    return 'validate';
  } catch (error) {
    return 'noValidate';
  }
};
